// MiseOne — per-user Dark Mode theme controller.
//
// Appearance preference is stored PER USER, both:
//   1. locally in localStorage as `miseone-theme:<user_id>` (fast, first-paint safe)
//   2. remotely on the user profile (`user.appearance_preference`) so it
//      follows the account across devices.
//
// A "guest" pre-login preference lives in `miseone-theme:guest` so the
// login page still respects the visitor's previous choice without ever
// spilling that value onto a signed-in account.
//
// The preload script in index.html only knows the guest key, so we
// briefly show the guest theme on cold-boot; AppContext then loads the
// current user and this hook re-applies the user-scoped preference.

import {
    createContext, useCallback, useContext, useEffect, useState,
} from "react";
import { useApp } from "@/context/AppContext";
import { api } from "@/lib/api";

const GUEST_KEY = "miseone-theme:guest";
const userKey = (uid) => `miseone-theme:${uid || "guest"}`;
const ThemeCtx = createContext(null);

function readSaved(userId) {
    try {
        // Read the user-scoped key first, then fall back to guest.
        const v = localStorage.getItem(userKey(userId))
                  || localStorage.getItem(GUEST_KEY)
                  || "light";
        return v === "dark" || v === "light" || v === "system" ? v : "light";
    } catch (_e) { return "light"; }
}

function apply(theme) {
    const root = document.documentElement;
    const prefersDark = window.matchMedia
        ? window.matchMedia("(prefers-color-scheme: dark)").matches
        : false;
    const dark = theme === "dark" || (theme === "system" && prefersDark);
    root.classList.toggle("dark", dark);
    root.dataset.theme = theme;
}

export function ThemeProvider({ children }) {
    const { user } = useApp();
    const uid = user?.id;
    const [theme, setThemeState] = useState(() => readSaved(uid));

    // Re-read the preference whenever the signed-in user changes.
    // Prefer the value stored on the user record (server-side) — falls
    // back to the local per-user cache, then guest.
    useEffect(() => {
        const remote = user?.appearance_preference;
        const next = remote && ["light", "dark", "system"].includes(remote)
            ? remote
            : readSaved(uid);
        setThemeState(next);
        // Refresh the local per-user cache so a browser refresh with the
        // same user restores the same theme without a round-trip.
        try { localStorage.setItem(userKey(uid), next); } catch (_e) {}
    }, [uid, user?.appearance_preference]);

    // Apply on every state change.
    useEffect(() => { apply(theme); }, [theme]);

    // Follow OS changes when the user chose "system".
    useEffect(() => {
        if (theme !== "system" || !window.matchMedia) return;
        const mq = window.matchMedia("(prefers-color-scheme: dark)");
        const onChange = () => apply("system");
        mq.addEventListener?.("change", onChange);
        return () => mq.removeEventListener?.("change", onChange);
    }, [theme]);

    const setTheme = useCallback((next) => {
        setThemeState(next);
        try {
            localStorage.setItem(userKey(uid), next);
            if (!uid) localStorage.setItem(GUEST_KEY, next);
        } catch (_e) {}
        // Persist server-side for signed-in users only. Fire-and-forget:
        // the local cache already keeps the choice sticky.
        if (uid) {
            api.patch("/auth/me", { appearance_preference: next })
                .catch(() => { /* local fallback is enough */ });
        }
    }, [uid]);

    return (
        <ThemeCtx.Provider value={{ theme, setTheme }}>
            {children}
        </ThemeCtx.Provider>
    );
}

export function useTheme() {
    const ctx = useContext(ThemeCtx);
    if (!ctx) throw new Error("useTheme must be used within ThemeProvider");
    return ctx;
}
